import React, {FC, useRef} from "react";
import * as Icon from "react-bootstrap-icons";
import {formatBytes} from "../../lib/helpers";
import {DropTargetMonitor, useDrag, useDrop} from 'react-dnd';
import {XYCoord} from 'dnd-core'
import {FileTypes} from "./FileTypes";

export interface FileProps {
  file: any
  index: number
  navigate: (file: any) => void
  editFile: (file: any) => void
  moveFile: (id: string, dragIndex: number, hoverIndex: number, dropped: boolean) => void
}

interface DragItem {
  index: number
  id: string
  type: string
}

const File: FC<FileProps> = ({file, index, navigate, editFile, moveFile}) => {
  const bytes = file.fileType === "folder" ? "" : `, ${formatBytes(file.size)}`;
  const icon = (file.fileType === "folder") ?
    <Icon.Folder size={30}/>
    : file.iconUrl ? <img src={file.iconUrl} width={40} height={40} alt={file.name}/>: <Icon.Image size={30}/>;
  const ref = useRef<HTMLDivElement>(null)

  const [{handlerId}, drop] = useDrop({
    accept: FileTypes.FILE,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },
    drop(item: DragItem, monitor: DropTargetMonitor) {
      if (!ref.current) {
        return
      }
      const dragIndex = item.index
      // actually perform the action
      moveFile(item.id, dragIndex, index, true)
    },
    hover(item: DragItem, monitor: DropTargetMonitor) {
      if (!ref.current) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = index

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect()

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2

      // Determine mouse position
      const clientOffset = monitor.getClientOffset()

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }

      // Time to actually perform the action
      moveFile(item.id, dragIndex, hoverIndex, false)

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex
    },
  });

  const id = file.id;
  const fileType = file.fileType

  const [{isDragging}, drag] = useDrag({
    type: FileTypes.FILE,
    item: () => {
      return {id, fileType, index}
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));

  return (
    <div className="file row py-2" ref={ref} style={{opacity}} data-handler-id={handlerId}
         onClick={() => navigate(file)}>
      <div className="col-10 align-items-baseline">
        {icon}<span
        className="mr-2"/>{file.name}{bytes}
      </div>
      <div className="col-2 text-right">
        <Icon.Pencil className="delete" onClick={(e) => {
          editFile(file);
          e.stopPropagation();
        }}/>
      </div>
    </div>
  );
}

export default File;
